import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

// ── PROJECTS ──────────────────────────────────────────────
export function useProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .order('id');
    if (!error) setProjects(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const updateProject = async (id, updates) => {
    const { error } = await supabase
      .from('projects')
      .update(updates)
      .eq('id', id);
    if (!error) fetch();
    return { error };
  };

  return { projects, loading, refetch: fetch, updateProject };
}

// ── DAILY LOG ─────────────────────────────────────────────
export function useDailyLog(projectId) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('daily_logs')
      .select('*')
      .eq('project_id', projectId)
      .order('log_date', { ascending: false });
    if (!error) setLogs(data || []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetch(); }, [fetch]);

  const addLog = async (entry) => {
    const { error } = await supabase
      .from('daily_logs')
      .insert({ ...entry, project_id: projectId });
    if (!error) fetch();
    return { error };
  };

  return { logs, loading, addLog, refetch: fetch };
}

// ── TODOS ─────────────────────────────────────────────────
export function useTodos(projectId) {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('todos')
      .select('*')
      .eq('project_id', projectId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: false });
    if (!error) setTodos(data || []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetch(); }, [fetch]);

  const addTodo = async (text, priority = 'medium') => {
    const { error } = await supabase
      .from('todos')
      .insert({ project_id: projectId, text, priority });
    if (!error) fetch();
    return { error };
  };

  const toggleTodo = async (id, done) => {
    const { error } = await supabase
      .from('todos')
      .update({ done, done_at: done ? new Date().toISOString() : null })
      .eq('id', id);
    // If this task was created from a client checklist item, complete that too
    if (!error && done) {
      const t = todos.find(x => x.id === id);
      if (t?.source_checklist_id) {
        await supabase.from('checklist_items').update({
          done: true, done_by: 'builder', done_at: new Date().toISOString(),
        }).eq('id', t.source_checklist_id);
        try {
          const { notifyProjectClients } = await import('./usePushNotifications');
          await notifyProjectClients(t.project_id, '✓ Completed from your list', (t.text || '').slice(0, 80), window.location.origin);
        } catch(e) {}
      }
    }
    if (!error) fetch();
    return { error };
  };

  const deleteTodo = async (id) => {
    const { error } = await supabase.from('todos').delete().eq('id', id);
    if (!error) fetch();
    return { error };
  };

  const reorderTodos = async (orderedTodos) => {
    // Assign sort_order by position. Batch updates in parallel, then refetch once.
    await Promise.all(orderedTodos.map((t, idx) =>
      supabase.from('todos').update({ sort_order: idx }).eq('id', t.id)
    ));
    fetch();
  };

  return { todos, loading, addTodo, toggleTodo, deleteTodo, reorderTodos, refetch: fetch };
}

// ── DECISIONS ─────────────────────────────────────────────
export function useDecisions(projectId) {
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('decisions')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });
    if (!error) setDecisions(data || []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetch(); }, [fetch]);

  const addDecision = async (decision) => {
    const { error } = await supabase
      .from('decisions')
      .insert({ ...decision, project_id: projectId });
    if (!error) fetch();
    return { error };
  };

  const updateDecision = async (id, updates) => {
    const { error } = await supabase
      .from('decisions')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (!error) fetch();
    return { error };
  };

  return { decisions, loading, addDecision, updateDecision, refetch: fetch };
}

// ── GANTT PHASES ──────────────────────────────────────────
export function useGantt(projectId) {
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('gantt_phases')
      .select('*')
      .eq('project_id', projectId)
      .order('sort_order');
    if (!error) setPhases(data || []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetch(); }, [fetch]);

  const togglePhase = async (id, checked) => {
    const { error } = await supabase
      .from('gantt_phases')
      .update({ checked })
      .eq('id', id);
    if (!error) fetch();
    return { error };
  };

  const updatePhaseStatus = async (id, status) => {
    const { error } = await supabase
      .from('gantt_phases')
      .update({ status })
      .eq('id', id);
    if (!error) fetch();
    return { error };
  };

  return { phases, loading, togglePhase, updatePhaseStatus, refetch: fetch };
}

// ── BUDGET ────────────────────────────────────────────────
export function useBudget(projectId) {
  const [categories, setCategories] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    const [catRes, expRes] = await Promise.all([
      supabase.from('budget_categories').select('*').eq('project_id', projectId).order('sort_order'),
      supabase.from('expenses').select('*').eq('project_id', projectId).order('expense_date', { ascending: false }),
    ]);
    if (!catRes.error) setCategories(catRes.data || []);
    if (!expRes.error) setExpenses(expRes.data || []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetch(); }, [fetch]);

  const addExpense = async (expense) => {
    const { error } = await supabase
      .from('expenses')
      .insert({ ...expense, project_id: projectId });
    if (!error) fetch();
    return { error };
  };

  return { categories, expenses, loading, addExpense, refetch: fetch };
}

// ── WEATHER ───────────────────────────────────────────────
export function useWeather(address) {
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    if (!address) return;
    const fetchWeather = async () => {
      try {
        const city = address.split(',')[1]?.trim() || address;
        const geo = await fetch(
          `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(city)}&count=1&language=en&format=json`
        );
        const gd = await geo.json();
        const r = gd.results?.[0];
        if (!r) return;
        const wr = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${r.latitude}&longitude=${r.longitude}&current_weather=true&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max&temperature_unit=fahrenheit&timezone=America%2FChicago&forecast_days=1`
        );
        const wd = await wr.json();
        const cw = wd.current_weather;
        setWeather({
          temp: Math.round(cw.temperature),
          code: cw.weathercode,
          hi: wd.daily?.temperature_2m_max?.[0] ? Math.round(wd.daily.temperature_2m_max[0]) : null,
          lo: wd.daily?.temperature_2m_min?.[0] ? Math.round(wd.daily.temperature_2m_min[0]) : null,
          rain: wd.daily?.precipitation_probability_max?.[0] ?? null,
          lat: r.latitude,
          lng: r.longitude,
          forecastUrl: `https://forecast.weather.gov/MapClick.php?lat=${r.latitude}&lon=${r.longitude}`,
        });
      } catch(e) { console.error('Weather error:', e); }
    };
    fetchWeather();
  }, [address]);

  return weather;
}
